<template>
  <div class="i-map-picker">
    <el-button type="primary" size="mini" @click="openMap">选择地址</el-button>
    <el-dialog
      title="选择地址"
      :visible.sync="showMap"
      width="720px"
      append-to-body
      :close-on-click-modal="false"
      @open="onDialogOpen"
    >
      <div class="search">
        <el-autocomplete
          v-model="keywords"
          placeholder="输入关键字搜索地点，或直接在地图上点击选点"
          clearable
          :trigger-on-focus="false"
          :fetch-suggestions="autoComplete"
          @select="chooseFromSearch"
          style="width: 100%;"
        />
      </div>
      <div class="map-container">
        <amap
          v-if="showMap"
          :zoom="zoom"
          :center="mapCenter"
          @click="onMapClick"
          @complete="onMapReady"
        >
          <amap-marker
            v-if="hasPosition"
            :position="mapCenter"
            :draggable="true"
            @dragend="onMarkerDragEnd"
          />
        </amap>
      </div>
      <div class="picked-info" v-if="hasPosition">
        <div class="info-row" v-if="pickedAddress"><span class="k">地址</span><span class="v">{{ pickedAddress }}</span></div>
        <div class="info-row"><span class="k">坐标</span><span class="v">{{ mapCenter[0].toFixed(6) }} , {{ mapCenter[1].toFixed(6) }}</span></div>
      </div>
      <div slot="footer">
        <el-button @click="showMap = false">取 消</el-button>
        <el-button type="primary" :disabled="!hasPosition" @click="confirmChoose">确 定</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
const DEFAULT_CENTER = [118.0897, 24.4798] // 厦门
const DEFAULT_ZOOM = 15

export default {
  name: 'i-map',
  props: {
    address: {
      type: Object,
      default() {
        return { position: null, name: '', address: '' }
      },
    },
  },
  data() {
    return {
      showMap: false,
      keywords: '',
      zoom: DEFAULT_ZOOM,
      mapCenter: DEFAULT_CENTER.slice(),
      hasPosition: false,
      pickedName: '',
      pickedAddress: '',
      geocoder: null,
    }
  },
  methods: {
    buildPositionFromProp() {
      const pos = this.address && this.address.position
      if (Array.isArray(pos) && pos.length === 2) {
        const lng = Number(pos[0])
        const lat = Number(pos[1])
        if (!Number.isNaN(lng) && !Number.isNaN(lat) && (lng !== 0 || lat !== 0)) {
          return [lng, lat]
        }
      }
      return null
    },
    openMap() {
      this.syncStateFromProp()
      this.showMap = true
    },
    onDialogOpen() {
      this.syncStateFromProp()
      this.$nextTick(() => {
        // 部分情况下 amap 初始化需要手动 setCenter，这里通过重新赋值触发更新
        this.mapCenter = this.mapCenter.slice()
      })
    },
    syncStateFromProp() {
      const pos = this.buildPositionFromProp()
      if (pos) {
        this.mapCenter = pos
        this.hasPosition = true
      } else {
        this.mapCenter = DEFAULT_CENTER.slice()
        this.hasPosition = false
      }
      this.pickedName = (this.address && this.address.name) || ''
      this.pickedAddress = (this.address && this.address.address) || ''
      this.keywords = this.pickedName
        ? (this.pickedAddress ? `${this.pickedName}（${this.pickedAddress}）` : this.pickedName)
        : (this.pickedAddress || '')
    },
    autoComplete(query, cb) {
      if (!query) { cb([]); return }
      if (typeof AMap === 'undefined' || !AMap.AutoComplete) { cb([]); return }
      const ac = new AMap.AutoComplete()
      ac.search(query, (status, result) => {
        if (status !== 'complete' || !result || !result.tips) { cb([]); return }
        const tips = result.tips
          .filter(t => t.location && t.location.lng && t.location.lat)
          .map(t => ({
            ...t,
            value: t.name + (t.address ? `（${t.district || ''}${t.address}）` : ''),
          }))
        cb(tips)
      })
    },
    chooseFromSearch(poi) {
      if (!poi || !poi.location) return
      const lng = Number(poi.location.lng)
      const lat = Number(poi.location.lat)
      if (Number.isNaN(lng) || Number.isNaN(lat)) return
      this.mapCenter = [lng, lat]
      this.hasPosition = true
      this.pickedName = poi.name || ''
      this.pickedAddress = (poi.district || '') + (poi.address || '')
    },
    onMapClick(e) {
      if (!e || !e.lnglat) return
      const lng = Number(e.lnglat.getLng ? e.lnglat.getLng() : e.lnglat.lng)
      const lat = Number(e.lnglat.getLat ? e.lnglat.getLat() : e.lnglat.lat)
      if (Number.isNaN(lng) || Number.isNaN(lat)) return
      this.mapCenter = [lng, lat]
      this.hasPosition = true
      this.reverseGeocode(lng, lat)
    },
    onMarkerDragEnd(e) {
      if (!e || !e.lnglat) return
      const lng = Number(e.lnglat.getLng ? e.lnglat.getLng() : e.lnglat.lng)
      const lat = Number(e.lnglat.getLat ? e.lnglat.getLat() : e.lnglat.lat)
      if (Number.isNaN(lng) || Number.isNaN(lat)) return
      this.mapCenter = [lng, lat]
      this.hasPosition = true
      this.reverseGeocode(lng, lat)
    },
    reverseGeocode(lng, lat) {
      if (typeof AMap === 'undefined') return
      const doGeo = () => {
        try {
          this.geocoder.getAddress([lng, lat], (status, result) => {
            if (status === 'complete' && result && result.regeocode) {
              const rg = result.regeocode
              this.pickedAddress = rg.formattedAddress || this.pickedAddress
              const poi = rg.pois && rg.pois[0]
              if (poi && poi.name) {
                this.pickedName = poi.name
              } else if (rg.addressComponent) {
                const ac = rg.addressComponent
                const building = (ac.building && ac.building.name) || ''
                if (building) this.pickedName = building
              }
              this.keywords = this.pickedName
                ? (this.pickedAddress ? `${this.pickedName}（${this.pickedAddress}）` : this.pickedName)
                : this.pickedAddress
            }
          })
        } catch (err) {
          // 忽略逆地理编码错误
        }
      }
      if (!this.geocoder) {
        if (AMap.Geocoder) {
          this.geocoder = new AMap.Geocoder()
          doGeo()
        } else if (AMap.plugin) {
          AMap.plugin(['AMap.Geocoder'], () => {
            this.geocoder = new AMap.Geocoder()
            doGeo()
          })
        }
      } else {
        doGeo()
      }
    },
    onMapReady() {
      // map 渲染完成，确保 center 被正确应用
    },
    confirmChoose() {
      if (!this.hasPosition) return
      this.showMap = false
      this.$emit('confirm', {
        position: this.mapCenter.slice(),
        name: this.pickedName || '',
        address: this.pickedAddress || '',
      })
    },
  },
}
</script>

<style scoped>
.search {
  padding-bottom: 12px;
}
.map-container {
  height: 400px;
  width: 100%;
  background: #f5f7fa;
}
.picked-info {
  margin-top: 12px;
  padding: 10px 14px;
  background: #fafafa;
  border: 1px solid #ebeef5;
  border-radius: 6px;
  font-size: 13px;
  color: #303133;
  line-height: 1.7;
}
.picked-info .info-row .k {
  display: inline-block;
  width: 50px;
  color: #909399;
}
.picked-info .info-row .v {
  color: #303133;
}
</style>



// WEBPACK FOOTER //
// src/components/i-map.vue